// File validation
export const validateFile = (file, options = {}) => {
  const {
    maxSize = process.env.REACT_APP_MAX_FILE_SIZE || 5242880, // 5MB default
    allowedTypes = (process.env.REACT_APP_ALLOWED_FILE_TYPES || 'image/jpeg,image/png,image/gif').split(','),
  } = options;

  if (!file) {
    throw new Error('No file provided');
  }

  if (file.size > maxSize) {
    throw new Error(`File size exceeds limit of ${maxSize / 1024 / 1024}MB`);
  }

  if (!allowedTypes.includes(file.type)) {
    throw new Error(`File type not allowed. Allowed types: ${allowedTypes.join(', ')}`);
  }

  return true;
};

// Input sanitization
export const sanitizeString = (str) => {
  if (typeof str !== 'string') return str;
  return str
    .trim()
    .replace(/[<>]/g, '') // Remove HTML tags
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+="[^"]*"/g, '') // Remove event handlers
    .replace(/on\w+='[^']*'/g, '') // Remove event handlers
    .replace(/data:/gi, '') // Remove data URIs
    .replace(/vbscript:/gi, ''); // Remove vbscript: protocol
};

// Password validation
export const validatePassword = (password) => {
  const minLength = 8;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  if (password.length < minLength) {
    throw new Error('Password must be at least 8 characters long');
  }
  if (!hasUpperCase) {
    throw new Error('Password must contain at least one uppercase letter');
  }
  if (!hasLowerCase) {
    throw new Error('Password must contain at least one lowercase letter');
  }
  if (!hasNumbers) {
    throw new Error('Password must contain at least one number');
  }
  if (!hasSpecialChar) {
    throw new Error('Password must contain at least one special character');
  }

  return true;
};

// Rate limiting helper
export const createRateLimiter = (options = {}) => {
  const {
    maxRequests = process.env.REACT_APP_RATE_LIMIT_REQUESTS || 100,
    duration = process.env.REACT_APP_RATE_LIMIT_DURATION || 3600, // 1 hour
  } = options;

  const requests = new Map();

  return {
    check: (key) => {
      const now = Date.now();
      const windowStart = now - duration * 1000;

      // Clean up old entries
      for (const [timestamp] of requests) {
        if (timestamp < windowStart) {
          requests.delete(timestamp);
        }
      }

      // Count requests in current window
      const requestCount = Array.from(requests.values())
        .filter(entry => entry.timestamp > windowStart && entry.key === key)
        .length;

      if (requestCount >= maxRequests) {
        return false;
      }

      requests.set(now, { key, timestamp: now });
      return true;
    },
  };
};

// XSS Protection
export const escapeHtml = (str) => {
  if (typeof str !== 'string') return str;
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

// CSRF Token generation and validation
export const generateCSRFToken = () => {
  const token = crypto.getRandomValues(new Uint8Array(32))
    .reduce((str, byte) => str + byte.toString(16).padStart(2, '0'), '');
  return token;
};

// Validate CSRF token
export const validateCSRFToken = (token, storedToken) => {
  if (!token || !storedToken) return false;
  return token === storedToken;
};

// Secure cookie options
export const secureCookieOptions = {
  httpOnly: true,
  secure: process.env.NODE_ENV === 'production',
  sameSite: 'strict',
  maxAge: 24 * 60 * 60, // 24 hours
}; 